import PublicLayout from "@/components/layout/PublicLayout";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { fetchSampleReport } from "@/lib/api";
import ReportFull from "@/components/report/ReportFull";
import { ArrowUpRight, FileText, ShieldCheck, ListChecks, DollarSign, Eye, Layers } from "lucide-react";
import InvestorDashboardPreview from "@/components/investor/preview/InvestorDashboardPreview";
import SampleReadAlongNavigator from "@/components/report/SampleReadAlongNavigator";
import { track } from "@/lib/analytics";

export default function SampleAnalysis() {
    const [report, setReport] = useState(null);
    const [previewTier, setPreviewTier] = useState(null);
    useEffect(() => {
        fetchSampleReport().then(setReport).catch(() => {});
    }, []);
    const openPreview = (tier) => {
        track("investor_preview_button_clicked", {
            tier,
            source: "sample_analysis_page",
        });
        setPreviewTier(tier);
    };
    const issueCount = report?.full_summary?.issues?.length ?? 9;
    return (
        <PublicLayout>
            <section className="bl-paper">
                <div className="bl-container pt-16 pb-10" data-testid="sample-page">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                        <div className="lg:col-span-7 min-w-0" data-reveal="slide-left-lg">
                            <div className="bl-eyebrow">Sample analysis</div>
                            <h1 className="mt-5 font-display font-semibold text-[1.75rem] sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.1] sm:leading-[1.05] lg:leading-[1.02] text-bl max-w-3xl">
                                What a finished Buyer's Leverage{" "}
                                <span className="font-italic-accent text-bl-primary">
                                    report
                                </span>{" "}
                                looks like.
                            </h1>
                            <p className="mt-6 text-bl-muted text-lg max-w-2xl leading-relaxed">
                                Below is a fully redacted Closing Strategy report for a fictional
                                property in Asheville, NC. Numbers are illustrative; structure
                                and tone are real.
                            </p>
                            <Link
                                to="/upload"
                                className="bl-btn-primary mt-7"
                                data-testid="sample-upload-cta"
                            >
                                Upload my inspection <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </div>

                        {/* Right-side: report stats glance */}
                        <div className="lg:col-span-5 min-w-0" data-reveal="slide-right" data-reveal-delay="2">
                            <div className="bl-card p-6 md:p-7" data-testid="sample-hero-side">
                                <div className="flex items-center justify-between">
                                    <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                        <FileText className="w-3.5 h-3.5" />
                                        Report snapshot
                                    </div>
                                    <span className="bl-badge bl-badge-info text-[10px]">
                                        Closing Strategy
                                    </span>
                                </div>

                                <div className="mt-5 grid grid-cols-2 gap-3">
                                    <div className="rounded-xl border border-bl px-4 py-3">
                                        <div className="text-[10px] tracking-[0.16em] uppercase text-bl-muted flex items-center gap-1">
                                            <ListChecks className="w-3 h-3" />
                                            Issues
                                        </div>
                                        <div className="mt-1 font-display text-2xl font-semibold text-bl">
                                            {issueCount}
                                        </div>
                                    </div>
                                    <div className="rounded-xl border border-bl px-4 py-3">
                                        <div className="text-[10px] tracking-[0.16em] uppercase text-bl-muted flex items-center gap-1">
                                            <DollarSign className="w-3 h-3" />
                                            Exposure
                                        </div>
                                        <div className="mt-1 font-display text-lg font-semibold text-bl">
                                            $15k–$35k
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-5 text-sm text-bl-muted leading-relaxed">
                                    Full sections include negotiation strategy, repair-priority
                                    timeline, calm clarity items, specialist follow-ups, and
                                    resources.
                                </div>

                                <div className="mt-4 flex items-center gap-2 text-xs text-bl-muted">
                                    <ShieldCheck className="w-3.5 h-3.5 text-bl-primary" />
                                    100% redacted · No real property data
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bl-container py-16" data-reveal>
                {report ? (
                    <ReportFull report={report} ownedTier="closing_strategy" isSample={true} />
                ) : (
                    <div className="bl-card p-10 text-center text-bl-muted">
                        <div className="bl-spinner mx-auto mb-4" />
                        Loading sample report…
                    </div>
                )}
            </section>

            <section
                className="bl-container pb-20"
                data-testid="sample-investor-preview-cta"
            >
                <div
                    className="bl-card p-6 sm:p-8 grid md:grid-cols-12 gap-6 items-center bg-bl-teal-tint/30 border-bl-primary/15"
                    data-reveal="rise-sm"
                >
                    <div className="md:col-span-7 min-w-0">
                        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-bl-primary font-medium">
                            <Layers className="w-3.5 h-3.5" />
                            Analyzing more than one property?
                        </div>
                        <h3 className="mt-2 font-display font-semibold text-xl sm:text-2xl text-bl">
                            See the Investor{" "}
                            <span className="font-italic-accent text-bl-primary">
                                Pro dashboard
                            </span>{" "}
                            in action.
                        </h3>
                        <p className="mt-2 text-sm text-bl-muted leading-relaxed max-w-xl">
                            This sample report is one finding. The Investor Pro
                            dashboard rolls findings across your full pipeline —
                            with deal comparison, negotiation leverage, and
                            repair-exposure analytics. Click below to explore.
                        </p>
                    </div>
                    <div className="md:col-span-5 flex flex-col gap-2">
                        <button
                            type="button"
                            onClick={() => openPreview("pro")}
                            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-medium bg-bl-primary text-bl-surface hover:bg-bl-primary-dark transition-colors min-h-[44px]"
                            data-testid="sample-investor-preview-pro-btn"
                        >
                            <Eye className="w-4 h-4" />
                            Preview the Investor Pro Dashboard
                        </button>
                        <Link
                            to="/pricing#investor-plans"
                            className="text-center text-xs text-bl-muted hover:text-bl-primary"
                            data-testid="sample-investor-pricing-link"
                        >
                            Or see all investor plans →
                        </Link>
                    </div>
                </div>
            </section>

            <InvestorDashboardPreview
                tier={previewTier || "pro"}
                open={!!previewTier}
                onOpenChange={(o) => {
                    if (!o) setPreviewTier(null);
                }}
                source="sample_analysis"
            />

            {/* Read-along navigator — only on the sample page so it doesn't
                duplicate the in-report TOC sidebar shown for paid reports. */}
            <SampleReadAlongNavigator surface="sample_analysis" />
        </PublicLayout>
    );
}
